import React, { useMemo, useRef, useState } from "react";
import { VoucherStatusSummary } from "../types";
import { MISChart } from "../components/MISChart";
import { statusColors } from "../chartTheme";
import { SectionToggle, SectionViewMode } from "../components/SectionToggle";
import { ExportButton } from "../components/ExportButton";
import { downloadChartImage, exportToCsv } from "../components/exportUtils";
import type { Chart as ChartJS } from "chart.js";

interface StatusSummarySectionProps {
  title: string;
  data: VoucherStatusSummary | null;
}

export const StatusSummarySection: React.FC<StatusSummarySectionProps> = ({ title, data }) => {
  const [mode, setMode] = useState<SectionViewMode>("table");
  const chartRef = useRef<ChartJS | null>(null);
  const rows = useMemo(() => {
    if (!data) return [];
    return [
      { label: "Issued", value: data.issued, ...statusColors.issued },
      { label: "Activated", value: data.activated, ...statusColors.activated },
      { label: "Used", value: data.used, ...statusColors.used },
      { label: "Expired", value: data.expired, ...statusColors.expired },
      { label: "Failed", value: data.failed, ...statusColors.failed },
      { label: "Revoked", value: data.revoked, ...statusColors.revoked },
    ];
  }, [data]);
  const graphRows = useMemo(() => rows.filter((r) => r.value !== 0), [rows]);

  if (!data) return null;

  const handleExportChart = () => {
    downloadChartImage(chartRef.current, "voucher-status-summary.png");
  };

  const handleExportTable = () => {
    exportToCsv(
      "voucher-status-summary.csv",
      ["Status", "Count"],
      rows.map((r) => [r.label, r.value])
    );
  };

  return (
    <div className="overflow-hidden border border-cardborder bg-white shadow-sm">
      <div className="border-b border-cardborder bg-gray-50 px-6 py-4">
        <SectionToggle
        title={title}
        subtitle="Voucher counts by status for the selected period"
        mode={mode}
        onChange={setMode}
        actions={
          <ExportButton
            label={mode === "graph" ? "Export PNG" : "Export CSV"}
            onClick={mode === "graph" ? handleExportChart : handleExportTable}
          />
        }
        />
      </div>
      <div className="px-6 py-6">
      {mode === "graph" ? (
        graphRows.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">No data to display</p>
        ) : (
        <MISChart
          ref={chartRef}
          type="bar"
          labels={graphRows.map((r) => r.label)}
          datasets={[
            {
              label: "Count",
              data: graphRows.map((r) => r.value),
              backgroundColor: graphRows.map((r) => r.bg),
              borderColor: graphRows.map((r) => r.border),
            },
          ]}
        />
        )
      ) : (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          {rows.map((it) => (
            <div
              key={it.label}
              className="flex flex-col justify-center border border-cardborder bg-gray-50 p-4"
            >
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {it.label}
              </span>
              <span className="mt-1.5 text-xl font-semibold text-gray-900">{it.value}</span>
            </div>
          ))}
        </div>
      )}
      </div>
    </div>
  );
};
